import { SearchIcon } from '@chakra-ui/icons';
import type { StylesConfig } from 'react-select';
import Select, {
  type ActionMeta,
  type CSSObjectWithLabel,
  components,
  type DropdownIndicatorProps,
  type GroupBase,
  type OnChangeValue,
  type OptionProps,
  type Props,
} from 'react-select';

import type { TreeItem } from '@revelio/iso-utility';

import { CustomOption } from './option';
import { useModifierKeyPressed } from './utils';

export type Option<T = unknown> = {
  entity?: T;
  value: string;
  label: string;
  isDisabled?: boolean;
  disabled?: boolean;
  limit?: number;
  info?: string;
};

const DropdownIndicator = <IsMulti extends boolean = true>(
  props: DropdownIndicatorProps<Option, IsMulti>
) => (
  <components.DropdownIndicator {...props}>
    <SearchIcon />
  </components.DropdownIndicator>
);
type CustomStyles = {
  [K in keyof StylesConfig<
    Option,
    true,
    GroupBase<Option>
  >]?: CSSObjectWithLabel;
};

export const SelectionListSelect = <IsMulti extends boolean = true>({
  customStyles,
  isSelectionListIdsEnabled,
  ...selectProps
}: Props<Option, IsMulti> & {
  customStyles?: CustomStyles;
  isSelectionListIdsEnabled?: boolean;
}) => {
  const isModifierPressed = useModifierKeyPressed();
  const showAdminId = Boolean(isSelectionListIdsEnabled) && isModifierPressed;
  const OptionComponent = (optionProps: OptionProps<Option, IsMulti>) => (
    <CustomOption {...optionProps} showAdminId={showAdminId} />
  );
  const handleChange = (
    allOptions: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (!selectProps.onChange) return;

    if (!Array.isArray(allOptions)) {
      selectProps.onChange(allOptions, actionMeta);
      return;
    }

    // Get limit from props as Select strips it out
    const getLimit = (opt: Option) => {
      const found = (selectProps.options as Option[]).find(
        (o) => o.value === opt.value
      );
      return found?.limit ?? Infinity;
    };

    const lowestLimit = Math.min(...allOptions.map(getLimit));

    // If we're under or at the limit, proceed normally
    if (allOptions.length <= lowestLimit) {
      selectProps.onChange(allOptions, actionMeta);
      return;
    }

    // If we're over the limit, keep only the most recent selections up to the limit
    const limitedSelection = allOptions.slice(-lowestLimit);
    selectProps.onChange(
      limitedSelection as unknown as OnChangeValue<Option, IsMulti>,
      actionMeta
    );
  };

  return (
    <Select<Option, IsMulti>
      menuIsOpen
      placeholder="Search..."
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isClearable={false}
      backspaceRemovesValue={false}
      components={{
        DropdownIndicator,
        Option: OptionComponent,
      }}
      styles={{
        container: (base) => ({ ...base, fontWeight: 400, fontSize: '14px' }),
        control: (base) => ({
          ...base,
          height: '32px',
          minHeight: '32px',
          borderColor: '#E2E8F0',
          borderRadius: '0.125rem',
          boxShadow: 'none',
          ':hover': { borderColor: '#CBD5E0' },
          ':focus-within': { borderColor: '#3dc679' },
        }),
        valueContainer: (base) => ({
          ...base,
          height: '100%',
          padding: '2px 12px',
        }),
        input: (base) => ({
          ...base,
          marginTop: 0,
          marginBottom: 0,
          cursor: 'text',
        }),
        indicatorsContainer: (base) => ({ ...base, height: '100%' }),
        indicatorSeparator: (base) => ({ ...base, display: 'none' }),
        dropdownIndicator: (base) => ({
          ...base,
          color: 'hsl(0, 0%, 80%)',
          ':hover': { color: 'hsl(0, 0%, 80%)' },
        }),
        menu: (base) => ({
          ...base,
          position: 'relative',
          margin: '10px 0 0',
          zIndex: 'unset',
          boxShadow: 'none',
          ...(customStyles?.menu ?? {}),
        }),
        menuList: (base) => ({
          ...base,
          padding: 0,
          fontSize: '12px',
          scrollbarColor: '#e3e3e3 #fff',
          color: '#2D426A',
        }),
        option: (base, { isFocused, isDisabled }) => ({
          ...base,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: '3px 4px',
          color: isDisabled ? '#A0AEC0' : '#2D426A',
          backgroundColor: isFocused ? '#F7FAFC' : 'transparent',
          borderColor: isFocused ? '#CBD5E0' : '#E2E8F0',
          ':active': { backgroundColor: '#EDF2F7' },
        }),
        groupHeading: (base) => ({
          ...base,
          marginBottom: '1px',
          fontSize: 'var(--chakra-fontSizes-xs)',
          color: '#96a0b5',
          fontWeight: '600',
          letterSpacing: '0.8px',
          textTransform: 'none',
          padding: '0px',
        }),
      }}
      {...selectProps}
      onChange={handleChange}
    />
  );
};

export const getSelectedTreeItemOptions = (
  selectedTreeItems: Record<string, TreeItem<string>> | undefined
): Option[] => {
  return Object.values(selectedTreeItems || {}).map((selected) => ({
    value: (selected.item?.id || '') as string,
    label: (selected.item?.longName ||
      selected.item?.shortName ||
      selected.item?.id ||
      '') as string,
  }));
};
